import { IFloorPlanModel } from '@api/models';
import { getAvailableCameraStorageList } from '@api/services/camera.service';
import FileUploadPreview from '@components/FileUploadPreview/FileUploadPreview';
import DragAndDropFileUpload from '@components/FormFields/DragAndDropFileUpload';
import { Button, Card, CircularProgress } from '@hyperflake/react-ui-library';
import { FormHelperText, Label } from '@library/forms';
import { useQuery } from '@tanstack/react-query';
import { ErrorMessage, Form, Formik } from 'formik';
import { FormikInput, FormikSelect } from 'modules/shared/features/formik';
import { timezoneList } from 'modules/shared/helpers/shared.constants';
import { FloorPlanStatusLabel } from 'modules/site-walk/constant/floorPlan.utils';
import useAddFloorPlanForm from 'modules/site-walk/hooks/useAddFloorPlanForm';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const AddFloorPlan = () => {
    const { clientId, projectId } = useParams();

    const navigate = useNavigate();

    const addFloorPlanForm = useAddFloorPlanForm({ clientId, projectId });

    const { data: storages, isLoading: storageIsLoading } = useQuery<string[]>(
        ['clients', clientId, 'projects', projectId, 'cameras', 'storage_names'],
        async () => {
            const data = getAvailableCameraStorageList(clientId, projectId);
            return data;
        }
    );

    const storageOption = useMemo(() => {
        if (!storages) return [];
        return [
            { label: 'Select Storage', value: '' },
            ...storages?.filter((val) => val !== 'AWS-us-west-2')?.map((val) => ({ label: val, value: val })),
        ];
    }, [storages]);

    const handleSubmit = async (
        payload: Pick<IFloorPlanModel, 'name' | 'file' | 'folder' | 'location' | 'status' | 'timezone' | 'storage'>
    ) => {
        try {
            await addFloorPlanForm.submit(payload);

            navigate(`/clients/${clientId}/projects/${projectId}/floor-plan`);

            toast.success('Floor Plan Added successfully.');
        } catch (ex: any) {
            toast.error(ex?.data?.message || 'Some error occured, please try again.');
            console.log(ex);
        }
    };

    return (
        <div>
            <Formik
                initialValues={addFloorPlanForm.initialValues}
                validationSchema={addFloorPlanForm.schema}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue }) => (
                    <Form>
                        <Card className="mt-4">
                            <Card.Header>
                                <h6>Basic Details</h6>
                            </Card.Header>

                            <Card.Body>
                                <div className=" grid grid-cols-1 md:grid-cols-2 ">
                                    <div className="col-span-1 md:col-span-2">
                                        <Label htmlFor="name">Name</Label>
                                        <FormikInput name="name" placeholder="Enter text here" />
                                        <FormHelperText variant="error">
                                            <ErrorMessage name="name" />
                                        </FormHelperText>
                                    </div>

                                    <div className="col-span-1 md:col-span-2">
                                        <Label>Storage</Label>
                                        <div className="relative w-full">
                                            <FormikSelect
                                                name="storage"
                                                options={
                                                    storageIsLoading
                                                        ? [{ label: 'Loading...', value: '' }]
                                                        : storageOption || []
                                                }
                                                disabled={storageIsLoading}
                                            />
                                            {storageIsLoading && (
                                                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-primary">
                                                    <span className="flex">
                                                        <CircularProgress size={16} />
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <FormHelperText variant="error">
                                            <ErrorMessage name="storage" />
                                        </FormHelperText>
                                    </div>

                                    <div className="col-span-1 md:mr-2">
                                        <Label>Latitude</Label>
                                        <FormikInput
                                            type="text"
                                            name="location.latitude"
                                            placeholder="Enter text here"
                                        />
                                        <FormHelperText variant="error">
                                            <ErrorMessage name="location.latitude" />
                                        </FormHelperText>
                                    </div>

                                    <div className="col-span-1 md:ml-2">
                                        <Label>Longitude</Label>
                                        <FormikInput
                                            type="text"
                                            name="location.longitude"
                                            placeholder="Enter text here"
                                        />
                                        <FormHelperText variant="error">
                                            <ErrorMessage name="location.longitude" />
                                        </FormHelperText>
                                    </div>

                                    <div className="col-span-1 md:col-span-2">
                                        <Label>Timezone</Label>
                                        <div className="w-full">
                                            <FormikSelect name="timezone" options={timezoneList} />
                                        </div>
                                        {/* <FormHelperText variant="error">
                                    <ErrorMessage name="timezone" />
                                </FormHelperText> */}
                                    </div>

                                    <div className="col-span-1 md:col-span-2 mt-4">
                                        <Label>Status</Label>
                                        <div className="w-full">
                                            <FormikSelect name="status" options={FloorPlanStatusLabel} />
                                        </div>
                                        <FormHelperText variant="error">
                                            <ErrorMessage name="status" />
                                        </FormHelperText>
                                    </div>

                                    <div className="col-span-1 md:col-span-2">
                                        <DragAndDropFileUpload
                                            text="Browse JPG/PNG/MP4"
                                            accept={{
                                                'image/*': ['.jpeg', '.jpg', '.png'],
                                                'video/*': [],
                                            }}
                                            onChange={(files) => setFieldValue('file', files[0])}
                                        />
                                    </div>

                                    {values.file && (
                                        <div className="col-span-1 md:col-span-2">
                                            <FileUploadPreview
                                                name={values.file.name}
                                                size={values.file.size}
                                                onCancel={() => setFieldValue('file', null)}
                                            />
                                        </div>
                                    )}

                                    <div className="col-span-1 md:col-span-2">
                                        <Button
                                            type="submit"
                                            size="lg"
                                            className="mt-4 w-full sm:w-32"
                                            disabled={addFloorPlanForm.isSubmitting}
                                        >
                                            {addFloorPlanForm.isSubmitting ? (
                                                <CircularProgress size={20} color="inherit" />
                                            ) : (
                                                'Submit'
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default AddFloorPlan;
